// Cover art for the pause cards. The "away" card usually shows the art of
// the player's dominant doctrine (their empire's identity), otherwise a
// random realm scene. Danger cards fit the danger: a city that has fallen
// gets a fallen-city scene, a city under attack a siege or assault scene.
// No card repeats the picture the previous one showed (ui/artPick.ts).
// Art lives in public/assets/images/pause: the first eleven were generated by
// scripts/generate-pause-covers.ts (`<id>-640.webp`), the rest through
// scripts/generate-images.mjs with configs/pause-covers-2.json (`<id>--640.webp`).

import type { Archetype } from '../ui/DoctrinePanel/archetypes.js';
import { markShown, pickFresh, wasJustShown } from '../ui/artPick.js';

export type PauseCoverKind = 'away' | 'danger';

export interface PauseCover {
  readonly id: string;
  readonly src: string;
  readonly srcSet: string;
  /** Accent colour for the card border, glow, eyebrow and primary button. */
  readonly accent: string;
  /** Eyebrow label, e.g. "Realm of Faith". */
  readonly label: string;
}

const BASE = '/assets/images/pause';

const DOCTRINE: Record<Archetype, { accent: string; label: string }> = {
  industry: { accent: '#f59e0b', label: 'Realm of Industry' },
  science: { accent: '#22d3ee', label: 'Realm of Science' },
  faith: { accent: '#facc15', label: 'Realm of Faith' },
  arcane: { accent: '#a78bfa', label: 'Realm of the Arcane' },
  nature: { accent: '#4ade80', label: 'Realm of Nature' },
  chaos: { accent: '#f43f5e', label: 'Realm of Chaos' },
};

export const REALM_COVERS = [
  'realm-dawn',
  'realm-harbor',
  'realm-pass',
  'realm-market',
  'realm-harvest',
  'realm-night',
] as const;
/** A city we owned is now someone else's. */
export const CAPTURED_COVERS = ['danger-fallen', 'danger-sack', 'danger-flight'] as const;
/** A city we still hold is under attack. */
export const ATTACKED_COVERS = [
  'danger-siege',
  'danger-march',
  'danger-assault',
  'danger-ramparts',
] as const;
export const DANGER_COVERS = [...CAPTURED_COVERS, ...ATTACKED_COVERS] as const;

/** Covers whose 640w file was written by the first generator (`-640`, not `--640`). */
const LEGACY_THUMB: ReadonlySet<string> = new Set([
  'industry',
  'science',
  'faith',
  'arcane',
  'nature',
  'chaos',
  'realm-dawn',
  'realm-harbor',
  'realm-pass',
  'danger-siege',
  'danger-march',
]);

/** Which kind of danger the card reports. */
export type DangerCoverKind = 'captured' | 'attacked';
const REALM_ACCENT = '#7dd3fc';
const DANGER_ACCENT = '#ef4444';

/** Share of "away" cards that show the dominant doctrine's art (vs a realm scene). */
export const DOCTRINE_COVER_SHARE = 0.7;

function cover(id: string, accent: string, label: string): PauseCover {
  return {
    id,
    src: `${BASE}/${id}.webp`,
    srcSet: `${BASE}/${id}${LEGACY_THUMB.has(id) ? '-640' : '--640'}.webp 640w, ${BASE}/${id}.webp 1280w`,
    accent,
    label,
  };
}

export function pickPauseCover(
  kind: PauseCoverKind,
  dominant: Archetype | null,
  rand: () => number = Math.random,
  danger: DangerCoverKind = 'attacked',
): PauseCover {
  if (kind === 'danger') {
    const pool = danger === 'captured' ? CAPTURED_COVERS : ATTACKED_COVERS;
    return cover(pickFresh(`pause:${danger}`, pool, rand), DANGER_ACCENT, 'Under threat');
  }
  // The doctrine's one picture shows 70% of the time, but never twice running.
  if (dominant && rand() < DOCTRINE_COVER_SHARE && !wasJustShown('pause:away', dominant)) {
    const d = DOCTRINE[dominant];
    markShown('pause:away', dominant);
    return cover(dominant, d.accent, d.label);
  }
  return cover(pickFresh('pause:away', REALM_COVERS, rand), REALM_ACCENT, 'Your realm');
}
